/**
 * Flags by Source Chart Component
 *
 * Displays a bar chart showing flag distribution by reputation source.
 */

import { useEffect, useState } from 'react';
import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { REPUTATION_SOURCE_COLORS } from '@/constants';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import type { FlagsBySourceData, ReputationSource } from '@/types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

// ============================================================================
// Component
// ============================================================================

export function FlagsBySourceChart() {
  const [data, setData] = useState<FlagsBySourceData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const company = useAuthStore((state) => state.company);

  useEffect(() => {
    if (!company?.id) {
      setIsLoading(false);
      return;
    }

    async function fetchFlagsBySource() {
      try {
        Logger.info(LogCategory.DATA, 'Loading flags by source data', 'FlagsBySourceChart');

        const { data: results, error } = await supabase
          .from('phone_check_results')
          .select('source')
          .eq('company_id', company!.id)
          .eq('status', 'flagged');

        if (error) throw error;

        // Count flags per source
        const sourceCounts = new Map<string, number>();
        results?.forEach((r: any) => {
          sourceCounts.set(r.source, (sourceCounts.get(r.source) || 0) + 1);
        });

        const total = results?.length || 0;

        const chartData: FlagsBySourceData[] = Array.from(sourceCounts.entries())
          .map(([source, count]) => ({
            source: source as ReputationSource,
            count,
            percentage: total > 0 ? (count / total) * 100 : 0,
          }))
          .sort((a, b) => b.count - a.count);

        setData(chartData);
      } catch (err) {
        Logger.error(LogCategory.DATA, 'Failed to load flags by source', 'FlagsBySourceChart', undefined, err);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchFlagsBySource();
  }, [company?.id]);

  const totalFlags = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <Card>
      <CardHeader
        title="Flags by Source"
        description={`Total flags: ${totalFlags.toLocaleString()}`}
      />
      <CardContent>
        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-pulse text-slate-400">Loading chart...</div>
          </div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal />
                <XAxis
                  type="number"
                  tick={{ fill: '#64748B', fontSize: 12 }}
                  tickLine={{ stroke: '#CBD5E1' }}
                  axisLine={{ stroke: '#CBD5E1' }}
                />
                <YAxis
                  dataKey="source"
                  type="category"
                  tick={{ fill: '#64748B', fontSize: 12 }}
                  tickLine={{ stroke: '#CBD5E1' }}
                  axisLine={{ stroke: '#CBD5E1' }}
                  width={75}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                  formatter={(value: number, _name: string, props: { payload?: FlagsBySourceData }) => {
                    const percentage = props.payload?.percentage?.toFixed(1) ?? '0';
                    return [`${value.toLocaleString()} (${percentage}%)`, 'Flags'];
                  }}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={REPUTATION_SOURCE_COLORS[entry.source]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Legend */}
        {!isLoading && (
          <div className="mt-4 flex flex-wrap gap-3">
            {data.slice(0, 5).map((item) => (
              <div key={item.source} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: REPUTATION_SOURCE_COLORS[item.source] }}
                />
                <span className="text-xs text-slate-600">
                  {item.source}: {item.count.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
